import React, { memo, type ReactElement, useCallback } from 'react';
import {
  ApplicationIcon,
  Avatar,
  Box,
  Chip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_WRN_BLD,
  TABS_FILTERS,
} from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import { getAbbreviatedNumber } from '../../utils';
import SodApplicationGroup from '../SodApplicationGroup';

import { WIDGET_KINDS } from './SodWidget';

import './SodWidgetListItem.css';

interface SodWidgetListItemProps {
  kind: string;
  tab: string | null;
  iconString?: string;
  text: string;
  count: number;
  entitlementEndpointLabel?: string;
  additionalInfo?: {
    expiryDate?: string;
    cost?: number;
    usageCount?: number;
    usageMax?: number;
    usagePercent?: number;
    isUpcoming?: boolean;
    spend?: number;
    percentOfAvg?: number;
    isAboveAverage?: boolean;
    wasteAmount?: number;
    countSuffix?: string;
    comparisonMetrics?: {
      label: string;
      percentOfAvg: number;
      isAboveAverage: boolean;
    }[];
  } | null;
  onClick: () => void;
}

/**
 * Renders a single list item within a SodWidget. Supports multiple
 * display variants (regular, user, endpoint, entitlement, tabbed metrics,
 * comparison metric) determined by the `kind` prop, each with distinct icon,
 * text, and count layouts.
 * @param {SodWidgetListItemProps} props - Component props
 * @param {string} props.kind - Widget kind determining the rendering variant
 * @param {string | null} props.tab - Active tab filter value, or null if no tab is active
 * @param {string} [props.iconString] - Icon identifier, logo key, or user name for avatar generation
 * @param {string} props.text - Primary display text for the list item
 * @param {number} props.count - Numeric count displayed in the item's right section
 * @param {string} [props.entitlementEndpointLabel] - Label for the application group in entitlement kind
 * @param {object | null} [props.additionalInfo] - Extra data for tabbed metrics and comparison metric kinds
 * @param {() => void} props.onClick - Click handler for the list item button
 * @returns {React.ReactElement} A button element styled as a list item row
 */
function SodWidgetListItem({
  kind,
  tab,
  iconString = undefined,
  text,
  count,
  entitlementEndpointLabel = undefined,
  additionalInfo = undefined,
  onClick,
}: SodWidgetListItemProps): ReactElement {
  const l = useInvertedLocalize();

  const getListItemIcon = useCallback(() => {
    if (kind === WIDGET_KINDS.COMPARISON_METRIC) {
      const isAbove = additionalInfo?.isAboveAverage ?? false;

      return (
        <div
          className={cx('Sod-WidgetListItem-comparisonBar', {
            'Sod-WidgetListItem-comparisonBar--critical': isAbove,
            'Sod-WidgetListItem-comparisonBar--primary': !isAbove,
          })}
        />
      );
    }

    if (kind === WIDGET_KINDS.TABBED_METRICS) {
      if (!iconString) {
        return <ApplicationIcon size='medium' />;
      }

      return <ApplicationIcon size='medium' logo={iconString} />;
    }

    if (!iconString) {
      if (kind === WIDGET_KINDS.ENDPOINT) {
        return <ApplicationIcon size='medium' />;
      }

      return null;
    }

    if (kind === WIDGET_KINDS.USER) {
      const userName = iconString.split(' ').filter(Boolean);
      const userInitials =
        userName.length >= 2
          ? userName[0].charAt(0).toUpperCase() +
            userName[userName.length - 1].charAt(0).toUpperCase()
          : 'NA';

      return <Avatar size='medium'>{userInitials}</Avatar>;
    }

    if (kind === WIDGET_KINDS.ENDPOINT) {
      return <ApplicationIcon size='medium' logo={iconString} />;
    }

    if (kind === WIDGET_KINDS.ENTITLEMENT) {
      return <ApplicationIcon icon='Entitlement' size='medium' />;
    }

    return <ApplicationIcon icon={iconString} size='medium' />;
  }, [kind, iconString, additionalInfo]);

  const getListItemText = useCallback(() => {
    if (kind === WIDGET_KINDS.ENTITLEMENT) {
      return (
        <>
          <Typography kind='body1' color={CF_NEU_BLD} lineLimit={1}>
            {text}
          </Typography>
          <SodApplicationGroup
            label={entitlementEndpointLabel ?? 'Application'}
            firstLogo={iconString}
          />
        </>
      );
    }

    if (kind === WIDGET_KINDS.TABBED_METRICS) {
      const isUpcoming = additionalInfo?.isUpcoming ?? false;

      return (
        <Box tag='div' className='Sod-WidgetListItem-textContent'>
          <Typography kind='body1' color={CF_NEU_BLD} lineLimit={1}>
            {text}
          </Typography>
          {additionalInfo?.expiryDate && (
            <Typography
              kind='body3'
              className={cx({
                upcomingClassName: isUpcoming,
                expiredClassName: !isUpcoming,
              })}
              color={isUpcoming ? CF_WRN_BLD : CF_CRT_MED}
              lineLimit={1}>
              {isUpcoming ? l('Expiry on') : l('Expired')}{' '}
              {additionalInfo.expiryDate}
            </Typography>
          )}
        </Box>
      );
    }

    if (kind === WIDGET_KINDS.COMPARISON_METRIC) {
      return (
        <Box tag='div' className='Sod-WidgetListItem-textContent'>
          <Typography kind='body1' color={CF_NEU_BLD} lineLimit={1}>
            {text}
          </Typography>
          {additionalInfo?.spend !== undefined && (
            <Typography
              kind='body3'
              color={CF_NEU_BLD}
              lineLimit={1}
              className='Comparison-Label'>
              {l('You spent')} {l(`$${additionalInfo.spend.toLocaleString()}`)}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Typography kind='body1' color={CF_NEU_BLD} lineLimit={2}>
        {text}
      </Typography>
    );
  }, [kind, text, iconString, entitlementEndpointLabel, additionalInfo, l]);

  const getListItemCount = useCallback(() => {
    if (
      (kind === WIDGET_KINDS.ENDPOINT || kind === WIDGET_KINDS.USER) &&
      additionalInfo?.wasteAmount !== undefined
    ) {
      return (
        <Typography kind='body1' color={CF_NEU_BLD}>
          {l(`$${additionalInfo.wasteAmount.toLocaleString()}`)}
        </Typography>
      );
    }

    if (kind === WIDGET_KINDS.ENDPOINT && tab === null) {
      const suffix = additionalInfo?.countSuffix
        ? ` ${additionalInfo.countSuffix}`
        : '';

      return (
        <Typography kind='body1' color={CF_NEU_BLD}>
          {count.toLocaleString()}
          {suffix}
        </Typography>
      );
    }

    if (kind === WIDGET_KINDS.TABBED_METRICS) {
      return (
        <Box tag='div' className='Sod-WidgetListItem-countContent'>
          {additionalInfo?.cost !== undefined && (
            <Typography
              kind='body3'
              color={CF_NEU_BLD}
              className='Count-Content'>
              {l(`${l('Cost')}: $${additionalInfo.cost.toLocaleString()}`)}
            </Typography>
          )}
          {additionalInfo?.usagePercent !== undefined && (
            <Typography
              kind='body3'
              color={CF_NEU_BLD}
              className='Count-Content'>
              {l(
                `${l('Usage')}: ${String(
                  additionalInfo.usageCount ?? 0
                )}/${String(additionalInfo.usageMax ?? 0)} (${String(
                  additionalInfo.usagePercent
                )}%)`
              )}
            </Typography>
          )}
        </Box>
      );
    }

    if (kind === WIDGET_KINDS.COMPARISON_METRIC) {
      // If comparisonMetrics array exists, render multiple chips
      if (
        additionalInfo?.comparisonMetrics &&
        Array.isArray(additionalInfo.comparisonMetrics) &&
        additionalInfo.comparisonMetrics.length > 0
      ) {
        return (
          <Box tag='div' className='Sod-WidgetListItem-chipsContainer'>
            {additionalInfo.comparisonMetrics.map((metric) => {
              const chipColor = metric.isAboveAverage ? 'critical' : 'success';

              return (
                <Chip
                  key={`${metric.label}-${String(metric.percentOfAvg)}`}
                  label={`${String(metric.percentOfAvg)}% of avg`}
                  kind='outlined'
                  color={chipColor}
                  size='small'
                />
              );
            })}
          </Box>
        );
      }

      // Fallback to single chip if no comparisonMetrics array
      const isAboveAverage = additionalInfo?.isAboveAverage ?? false;
      const chipColor = isAboveAverage ? 'critical' : 'success';

      return (
        <Chip
          label={`${String(additionalInfo?.percentOfAvg)}% of avg`}
          kind='outlined'
          color={chipColor}
          size='small'
        />
      );
    }

    if (tab !== null) {
      const activeTabObject = Object.values(TABS_FILTERS).find(
        (item) => item.value === tab
      );
      const label = `${count} ${
        activeTabObject ? l(activeTabObject.label) : ''
      } ${l('Violations')}`;

      return (
        <Typography
          kind='h1'
          color={CF_NEU_BLD}
          tooltipConfigs={{
            text: label,
            placement: 'top',
          }}
          showTooltipOnHover>
          {getAbbreviatedNumber(count)}
        </Typography>
      );
    }

    return null;
  }, [l, tab, count, kind, additionalInfo]);

  return (
    <button
      type='button'
      onClick={onClick}
      className='Sod-WidgetListItem-wrapper'>
      <Box tag='div' className='Sod-WidgetListItem-iconBox'>
        {getListItemIcon()}
      </Box>
      <Box tag='div' className='Sod-WidgetListItem-textBox'>
        {getListItemText()}
      </Box>
      <Box tag='div' className='Sod-WidgetListItem-countBox'>
        {getListItemCount()}
      </Box>
    </button>
  );
}

SodWidgetListItem.defaultProps = {
  iconString: undefined,
  entitlementEndpointLabel: undefined,
  additionalInfo: null,
};

export default memo(SodWidgetListItem);
